import { Component, OnInit } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  UntypedFormBuilder,
  UntypedFormArray,
} from '@angular/forms';
import { MobileLengthPerCode } from '@shared-features/enums/mobile-length-per-code.enum';
import { Receiver } from '../../../receivers/models/receiver';
import { BaseComponent } from '@shared/components/base-component/base.component';
import {
  EmailValidator,
  MobileNumberLengthValidator,
  PhoneNumberTypeValidator,
  PhoneNumberValidator,
} from '@shared/validators/form-validators';
import { ConfigConstant } from '@shared/constants/config.constant';
import { LocationType } from '@shared/enums/location-type.enum';
import { ReceiverType } from '@shared-features/enums/receiver-type.enum';
import { Router, ActivatedRoute } from '@angular/router';
import { FunctionUtil } from '@shared/utils/function.util';
import { TabNavigationService } from '@shared/services/tab-navigation.service';
import { CustomerTabConfig } from '@shared-features/enums/customer-tab-config.enum';
import { TabGroup } from '@shared/enums/tab-group.enum';
import { RoutesUtil } from '../../utils/routes.util';
import { Country } from '@shared-features/models/country';
import { ReceiversCommonApiService } from '@shared-features/services/receivers-common-api';
import { SharedApiService } from '@shared-features/services/shared-api.service';

@Component({
  selector: 'app-add-receiver',
  templateUrl: './add-receiver.component.html',
  styleUrls: ['./add-receiver.component.scss'],
})
export class AddReceiverComponent extends BaseComponent implements OnInit {
  customerId: number;
  receiverType: ReceiverType;

  form: UntypedFormGroup;
  contacts: UntypedFormArray;
  selectedCountryCode: any;
  countries: Country[] = [];
  addedReceiver: Receiver = new Receiver();
  LocationType = LocationType;
  locationIds: number[] = [];
  MobileLengthPerCode = MobileLengthPerCode;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private tabNavigationService: TabNavigationService,
    private sharedApiService: SharedApiService,
    private receiversCommonApiService: ReceiversCommonApiService
  ) {
    super();
  }

  ngOnInit(): void {
    this.receiverType = this.activatedRoute.snapshot.data?.receiverType;
    const id = this.activatedRoute.snapshot.params?.id;
    if (id) this.customerId = parseInt(id);
    this.getAllCountries();
  }

  private getAllCountries(): void {
    this.load(this.sharedApiService.getAllCountries(), {
      isLoadingTransparent: true,
      fetchOnce: true,
    }).subscribe((data) => {
      this.countries = data;
      this.initForm();
    });
  }

  getLocationIds(event) {
    this.locationIds.push(event);
  }

  onCancelButtonClicked() {
    if (this.customerId) {
      this.router.navigate([RoutesUtil.CustomersB2BView.url({ id: this.customerId })]);
      this.tabNavigationService.activatedTab$.next({
        group: TabGroup.Customer,
        index: CustomerTabConfig.receivers,
      });
    } else {
      this.router.navigate([RoutesUtil.Receivers.url()]);
    }
    this.form.reset();
  }

  onSaveButtonClicked() {
    this.resetAddedReceiver();
    this.addedReceiver.name = this.form.value['companyName'];
    this.form.value['contacts'].forEach((contact, index) => {
      this.addedReceiver.contactPerson.push({
        firstName: contact.firstName,
        lastName: contact.lastName,
        email: contact.email,
        mobileNumber: contact.mobile?.toString()
          ? contact.countryCode?.toString() +
            ConfigConstant.MOBILE_NUMBER_SEPARATOR +
            contact.mobile?.toString()
          : null,
        isDefault: index === 0 ? true : null,
        dropDownDisplayName: null,
        dropDownValue: null,
      });
    });
    this.addReceiver();
  }

  resetAddedReceiver() {
    this.addedReceiver.name = null;
    this.addedReceiver.contactPerson = [];
  }

  private addReceiver(): void {
    this.load(
      this.receiversCommonApiService.addReceiver(
        this.addedReceiver,
        this.receiverType,
        this.locationIds,
        this.customerId
      ),
      { isLoadingTransparent: true, fetchOnce: true }
    ).subscribe(() => {
      this.asyncFeedbackService.showSuccessMessage(
        this.translationsList['SuccessMessages']['ReceiverAddedSuccessfully']
      );
      this.onCancelButtonClicked();
    });
  }

  createContact(): UntypedFormGroup {
    const group = this.formBuilder.group({
      firstName: new UntypedFormControl('', [Validators.required]),
      lastName: new UntypedFormControl('', [Validators.required]),
      email: new UntypedFormControl('', [EmailValidator]),
      mobile: new UntypedFormControl(''),
      countryCode: new UntypedFormControl(FunctionUtil.getSaudiArabiaPhoneKey(this.countries), [
        Validators.required,
      ]),
    });
    group.controls.mobile.setValidators([
      PhoneNumberValidator,
      PhoneNumberTypeValidator,
      MobileNumberLengthValidator(() => group?.controls.countryCode.value || ''),
    ]);
    return group;
  }

  addContact(): void {
    this.contacts = this.form.get('contacts') as UntypedFormArray;
    this.contacts.push(this.createContact());
  }

  private initForm() {
    this.selectedCountryCode = this.countries[0];

    this.form = this.formBuilder.group({
      companyName: new UntypedFormControl('', [Validators.required]),
      contacts: this.formBuilder.array([this.createContact()]),
    });
  }
}
